import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useForm, CreateSubAccountRequest, TextField, Tooltip } from 'vopay';
import { FieldData } from '@/components/Form/types';
import SelectRoleField from '../../SubAccountCreation/SelectRoleField';
import { usePermissions } from '@/providers/Permissions.provider';

type UseSubAccountCreationFormConfigArgs = {
  onSubmit: (values: CreateSubAccountRequest) => void | Promise<void>;
  /**
   * Account token of the account the sub-account will be created under, when
   * that isn't the logged-in account (the org-chart dialog creates under an
   * arbitrary node). Scopes the role dropdown to that account's roles.
   */
  bearer?: string;
  /** Renders every field read-only — for the standalone page's review step. */
  isReviewing?: boolean;
};

// Shared useForm setup for both sub-account creation surfaces (the standalone
// page and the org-chart dialog). Encapsulates the field config, initial
// values, and useForm wiring so consumers only supply onSubmit and render —
// they never touch useForm or the FieldData list themselves.
export default function useSubAccountCreationFormConfig({
  onSubmit,
  bearer,
  isReviewing,
}: UseSubAccountCreationFormConfigArgs) {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.Create.Form' });
  const { permissions } = usePermissions();

  const config: FieldData<CreateSubAccountRequest>[] = [
    {
      label: () => t('LegalBusinessName.label'),
      fieldName: 'LegalBusinessName',
      getInputState: () => 'required',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<CreateSubAccountRequest['LegalBusinessName']>({});

        return <TextField placeholder={t('LegalBusinessName.placeholder')} variant="outlineShadow" {...fieldData} />;
      },
      showField: () => true,
      validate: () => Yup.mixed().required(),
    },
    {
      label: () => t('OriginatorName.label'),
      fieldName: 'OriginatorName',
      getInputState: () => 'required',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<CreateSubAccountRequest['OriginatorName']>({});

        return (
          <TextField
            placeholder={t('OriginatorName.placeholder')}
            variant="outlineShadow"
            {...fieldData}
            maxLength={30}
          />
        );
      },
      showField: () => true,
      tooltip: () => <Tooltip information={t('OriginatorName.information')} position="top" />,
      validate: () => Yup.string().required().max(30, t('OriginatorName.information')),
    },
    {
      label: () => t('OriginatorShortName.label'),
      fieldName: 'OriginatorShortName',
      getInputState: () => 'required',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<CreateSubAccountRequest['OriginatorShortName']>({});

        return (
          <TextField
            placeholder={t('OriginatorShortName.placeholder')}
            variant="outlineShadow"
            {...fieldData}
            maxLength={15}
          />
        );
      },
      showField: () => true,
      tooltip: () => <Tooltip information={t('OriginatorShortName.information')} position="top" />,
      validate: () => Yup.string().required().max(15, t('OriginatorShortName.information')),
    },
    {
      label: () => t('EmailAddress.label'),
      fieldName: 'EmailAddress',
      getInputState: () => 'required',
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<CreateSubAccountRequest['EmailAddress']>({});

        return (
          <TextField
            placeholder={t('EmailAddress.placeholder')}
            {...fieldData}
            preset="email"
            variant="outlineShadow"
          />
        );
      },
      showField: () => true,
      tooltip: () => <Tooltip information={t('EmailAddress.information')} position="top" />,
      validate: () => Yup.string().email().required(),
    },
    {
      // Role picker, gated on ViewUserRoles. The selected RoleID rides along
      // with the rest of the form; the backend assigns it to the new
      // sub-account's first user. serializeField omits it when left empty.
      fieldName: 'RoleID',
      label: () => t('SelectRole.label', { defaultValue: 'Select Role' }),
      getInputState: () => 'optional',
      showField: () => !!permissions.ViewUserRoles,
      serializeField: (values) => values.RoleID || undefined,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<CreateSubAccountRequest['RoleID']>({});

        return (
          <SelectRoleField
            roleID={ctx.values.RoleID ?? null}
            bearer={bearer}
            onChange={(roleID) => ctx.setFieldValue('RoleID', roleID ?? '')}
            disabled={fieldData.disabled}
          />
        );
      },
    },
  ];

  const { ctx, renderFormElements } = useForm<CreateSubAccountRequest>({
    config,
    initialValues: {
      LegalBusinessName: '',
      OriginatorName: '',
      OriginatorShortName: '',
      EmailAddress: '',
      RoleID: '',
    },
    onSubmit,
    isReviewing,
  });

  return {
    ctx,
    renderFormElements: () => renderFormElements({ config }),
  };
}
